/**
 * Shared protocol module for the host-rendered ad bridge (ADR-001).
 *
 * Both sides — the game bundle's BridgeClient and the host shell's
 * HostController — import this file. It defines the namespaced, versioned
 * envelope, the message-type union, the ad_result outcome enum, and pure
 * build/parse/validate helpers. No DOM/window access here so it is safe to
 * import from either bundle and to unit-test under a node test env.
 *
 * Envelope shape (frozen at v=1 — bump the version on any breaking change):
 *
 *   { __getjarAds: true, v: 1, type, correlationId, payload }
 *
 * All cross-frame messages MUST round-trip through `buildMessage` /
 * `parseMessage`. parseMessage is intentionally strict: anything that does not
 * carry the namespace marker, version, a known type, and a non-empty
 * correlationId is rejected — origin checks are done by the caller, but the
 * structural validation always lives here.
 */
/** The discriminator field that marks an envelope as ours. */
export const BRIDGE_NAMESPACE = '__getjarAds';
/**
 * Wire-protocol version. Receiver rejects any envelope whose `v` does not
 * match this constant — there is no negotiation. Bumping requires shipping
 * matching builds on both sides.
 */
export const BRIDGE_VERSION = 1;
const ALL_TYPES = new Set([
    'handshake',
    'query_ready',
    'request_show',
    'handshake_ack',
    'ready_state',
    'ad_result',
]);
/**
 * Build a wire-ready envelope. Pure; safe to call from either side.
 */
export function buildMessage(type, correlationId, payload) {
    return {
        [BRIDGE_NAMESPACE]: true,
        v: BRIDGE_VERSION,
        type,
        correlationId,
        payload,
    };
}
/**
 * Strict envelope validator. Returns a discriminated union so callers must
 * handle the failure branch explicitly. Anything missing the namespace, on
 * the wrong protocol version, with an unknown `type`, or with no
 * correlationId is rejected — this is the structural gate; origin checks are
 * the caller's responsibility.
 */
export function parseMessage(raw) {
    if (raw === null || typeof raw !== 'object')
        return { ok: false, reason: 'not_object' };
    const m = raw;
    if (m[BRIDGE_NAMESPACE] !== true)
        return { ok: false, reason: 'not_bridge_message' };
    if (m.v !== BRIDGE_VERSION)
        return { ok: false, reason: 'wrong_version' };
    if (typeof m.type !== 'string' || !ALL_TYPES.has(m.type)) {
        return { ok: false, reason: 'invalid_type' };
    }
    if (typeof m.correlationId !== 'string' || m.correlationId.length === 0) {
        return { ok: false, reason: 'missing_correlation' };
    }
    return { ok: true, message: m };
}
/**
 * Lightweight correlation-id generator. We don't need cryptographic strength
 * — the id only needs to be unique inside a single page lifetime and
 * unpredictable enough that two concurrent requests don't collide.
 */
export function newCorrelationId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
